import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "90vw",
    maxHeight: "90vh",
    width: "auto",
    height: "auto",
    aspectRatio: "3/4",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "95vw",
      maxHeight: "95vh",
      aspectRatio: "9/16",
    },
  },
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  backgroundColor: "rgba(255,255,255,0.8)",
  zIndex: 1000,
  transform: "translateY(-50%)",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.9)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5),
    "& svg": {
      fontSize: "1rem",
    },
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "50vh",
  [theme.breakpoints.down("sm")]: {
    minHeight: "40vh",
  },
}));

interface ImageGalleryModalProps {
  open: boolean;
  onClose: () => void;
  images: string[];
  startIndex: number;
}

export const ImageGalleryModal: React.FC<ImageGalleryModalProps> = ({
  open,
  onClose,
  images,
  startIndex,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Reset index when images change
  useEffect(() => {
    setCurrentIndex(startIndex || 0);
  }, [images, startIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isSmallScreen}
    >
      <DialogTitle sx={{ pb: 1, pr: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>
            Photo {currentIndex + 1} of {images.length}
          </span>
          <IconButton
            onClick={onClose}
            size={isSmallScreen ? "medium" : "small"}
            sx={{ ml: 2 }}
          >
            <CloseIcon fontSize={isSmallScreen ? "medium" : "small"} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          position: "relative",
          p: 0,
          overflow: "hidden",
          flex: 1,
        }}
      >
        <ImageContainer>
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={3}
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{
              mode: "toggle",
              step: isSmallScreen ? 0.5 : 0.7,
            }}
            limitToBounds={false}
          >
            <TransformComponent
              wrapperStyle={{
                width: "100%",
                height: "100%",
              }}
              contentStyle={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={images[currentIndex]}
                alt={`Installation pic ${currentIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  userSelect: "none",
                }}
              />
            </TransformComponent>
          </TransformWrapper>

          {images.length > 1 && (
            <>
              <NavigationButton
                onClick={handlePrev}
                sx={{ left: isSmallScreen ? 4 : 8 }}
                size={isSmallScreen ? "medium" : "small"}
              >
                <ArrowBackIosNewIcon
                  fontSize={isSmallScreen ? "medium" : "small"}
                />
              </NavigationButton>
              <NavigationButton
                onClick={handleNext}
                sx={{ right: isSmallScreen ? 4 : 8 }}
                size={isSmallScreen ? "medium" : "small"}
              >
                <ArrowForwardIosIcon
                  fontSize={isSmallScreen ? "medium" : "small"}
                />
              </NavigationButton>
            </>
          )}
        </ImageContainer>
      </DialogContent>
    </StyledDialog>
  );
};
